import { apiClient } from "./client";
import type {
  ApiResponse,
  AppliedJobsQuery,
  JobQuestionsData,
  CreatePaymentSessionPayload,
  JobQuestionsQuery,
  LoginPayload,
  RegisterPayload,
} from "@/types";

// ─── Auth ─────────────────────────────────────────────────────────────────────

export function extractApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null
  ) {
    const response = (error as { response?: { data?: { message?: unknown } } })
      .response;
    if (
      typeof response?.data?.message === "string" &&
      response.data.message.trim()
    ) {
      return response.data.message;
    }
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string" &&
    (error as { message?: string }).message?.trim()
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
}

export async function getJobQuestions({
  page,
  limit,
  search,
  type,
  token,
  signal,
}: JobQuestionsQuery & { token: string; signal?: AbortSignal }): Promise<
  ApiResponse<JobQuestionsData>
> {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));

  const normalizedSearch = search?.trim();
  const normalizedType = type?.trim();

  if (normalizedSearch) {
    params.set("search", normalizedSearch);
  }
  if (normalizedType) {
    params.set("type", normalizedType);
  }

  const response = await apiClient.get<ApiResponse<JobQuestionsData>>(
    `/job-question/get-all?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    },
  );

  return response.data;
}
export const authService = {
  login: (p: LoginPayload) => apiClient.post("/auth/login", p),
  register: (p: RegisterPayload) => apiClient.post("/auth/register", p),
  verifyOtp: (p: { email: string; otp: string }) =>
    apiClient.post("/auth/verify-otp", p),
  resendOtp: (p: { email: string }) => apiClient.post("/auth/resend-otp", p),
  resetPassword: (p: { email: string; otp: string; newPassword: string }) =>
    apiClient.post("/auth/reset-password", p),
  refreshToken: (p: { refreshToken: string }) =>
    apiClient.post("/auth/refresh-token", p),
  updatePassword: (
    p: { oldPassword: string; newPassword: string },
    email: string,
  ) =>
    apiClient.post("/auth/update-password", {
      email,
      currentPassword: p.oldPassword,
      newPassword: p.newPassword,
      confirmNewPassword: p.newPassword,
    }),
  updateProfile: (fd: FormData) =>
    apiClient.put("/auth/update-profile", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// ─── Profile ──────────────────────────────────────────────────────────────────
export const profileService = {
  get: () => apiClient.get("/profile"),
  update: (p: Record<string, unknown>) => apiClient.put("/profile/update", p),
};

// ─── Job Profile ──────────────────────────────────────────────────────────────
export const jobProfileService = {
  create: (p: Record<string, unknown>) =>
    apiClient.post("/job-profile/create", p),
  show: () => apiClient.get("/job-profile/show"),
  uploadResume: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post("/job-profile/upload-resume", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadResumeUrl: (resumeUrl: string) =>
    apiClient.post("/job-profile/upload-resume", { resumeUrl }),
  uploadCoverLetter: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post("/job-profile/upload-cover-letter", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getAiAnswer: (p: { question: string; context?: string }) =>
    apiClient.post("/job-profile/ai-answer", p),
  getResumeSkills: () => apiClient.get("/job-profile/resume-skill"),
  getExperience: () => apiClient.get("/job-profile/get-experience"),
  updateSkills: (p: { skills: string[] }) =>
    apiClient.post("/job-profile/update-skills", p),
  updateExperiences: (p: unknown[]) =>
    apiClient.post("/job-profile/update-experiences", p),
  updateNegativeKeywords: (p: { keywords: string[] }) =>
    apiClient.post("/job-profile/update-negative-keywords", p),
  getNegativeKeywords: () => apiClient.get("/job-profile/negative-keywords"),
  getContactInfo: () => apiClient.get("/job-profile/contact-info"),
  parseJobCollection: (p: { jobDescription: string }) =>
    apiClient.post("/job-profile/parse-job-collection", p),
  enhanceAndGeneratePdf: (p: {
    jobDescriptionHash: string;
    templateId?: string;
  }) => apiClient.post("/job-profile/enhance-resume-generate-pdf", p),
  calculateResumeScore: (p: Record<string, unknown>) =>
    apiClient.post("/job-profile/resume-score", p),
  saveResumeScore: (p: {
    jobId: string;
    scoreData: unknown;
    resumeLink?: string;
  }) => apiClient.post("/job-profile/save-resume-score", p),
  getResumeLimitStatus: () => apiClient.get("/job-profile/resume-limit-status"),
};

// ─── Applied Jobs ─────────────────────────────────────────────────────────────
export const appliedJobsService = {
  create: (p: Record<string, unknown>) =>
    apiClient.post("/applied-job/create", p),
  getAll: (params?: AppliedJobsQuery) =>
    apiClient.get("/applied-job/get-all", { params }),
  update: (id: string, p: Record<string, unknown>) =>
    apiClient.put(`/applied-job/update/${id}`, p),
};

// ─── Job Questions ────────────────────────────────────────────────────────────
export const jobQuestionService = {
  create: (p: Record<string, unknown>) =>
    apiClient.post("/job-question/create", p),
  update: (id: string, p: Record<string, unknown>) =>
    apiClient.put(`/job-question/update/${id}`, p),
  show: (id: string) => apiClient.get(`/job-question/show/${id}`),
  delete: (id: string) => apiClient.delete(`/job-question/delete/${id}`),
  getAll: (
    params: JobQuestionsQuery & { token: string; signal?: AbortSignal },
  ) => getJobQuestions(params),
  getMatch: (p: { question: string }) =>
    apiClient.post("/job-question/get-match", p),
  getAllAnswers: () => apiClient.get("/job-question/get-all-answers"),
  markViewed: (id: string) =>
    apiClient.patch(`/job-question/mark-viewed/${id}`),
};

// ─── Subscription ─────────────────────────────────────────────────────────────
export const subscriptionService = {
  getPlans: () => apiClient.get("/subscription/get-plans"),
  getPlan: (planId: string) => apiClient.get(`/subscription/plan/${planId}`),
  createPaymentSession: (p: CreatePaymentSessionPayload) =>
    apiClient.post("/subscription/create-payment-session", p),
  createBillingSession: () =>
    apiClient.post("/subscription/create-billing-session"),
  validateToken: (platform: string) =>
    apiClient.get(`/subscription/token-validator/${platform}`),
};

// ─── Platform Credentials ─────────────────────────────────────────────────────
export const platformCredentialsService = {
  save: (p: { platform: string; email: string; password: string }) =>
    apiClient.post("/platform-credentials", p),
  get: (params?: { platform?: string }) =>
    apiClient.get("/platform-credentials", { params }),
  getAll: () => apiClient.get("/platform-credentials/all"),
  delete: (platform: string) =>
    apiClient.delete("/platform-credentials", { data: { platform } }),
};

// ─── Events / Analytics ───────────────────────────────────────────────────────
export const eventsService = {
  getStats: (params?: { dateRange?: string; platform?: string }) =>
    apiClient.get("/events/stats", { params }),
};

// ─── Contact ──────────────────────────────────────────────────────────────────
export const contactService = {
  create: (p: { name: string; email: string; message: string }) =>
    apiClient.post("/contactus/create", p),
};
